/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      this.hydratePrettyUrl();
    }

    /**
     * Shopify filter fetches stay on native `filter.*` params (including
     * FilterSettingGroup GIDs). The address bar uses short keys + labels.
     */
    hydratePrettyUrl() {
      if (!this.form || !this.filteringEnabled) return;

      const raw = window.location.search.startsWith('?')
        ? window.location.search.slice(1)
        : window.location.search;
      if (!raw) return;

      const params = new URLSearchParams(raw);
      const prettyKeys = FacetFilters.prettyKeySet(this.form);
      const hasShopifyFilter = [...params.keys()].some((key) => key.startsWith('filter.'));
      const hasPretty = [...params.keys()].some((key) => prettyKeys.has(key));

      if (hasShopifyFilter) {
        FacetFilters.updateURL(raw, { replace: true });
        return;
      }

      if (hasPretty) {
        const shopify = FacetFilters.toShopifyParams(raw, this.form);
        if (shopify !== raw) this.applyFilters(shopify, null, true, true);
      }
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters?.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

        if (!this.clearAllHandler) {
          this.clearAllHandler = this.handleClearAllClick.bind(this);
          this.form.addEventListener('click', this.clearAllHandler);
        }

        if (!this.resetFiltersHandler) {
          this.resetFiltersHandler = this.handleResetFiltersClick.bind(this);
          this.querySelector('.js-reset-filters')?.addEventListener('click', this.resetFiltersHandler);
        }
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
        this.expandDefaultFilters();
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
        this.collapseFilters();
      }
    }

    /**
     * Expands filters marked data-expand-default (desktop only).
     */
    expandDefaultFilters() {
      this.querySelectorAll('.facets__filters details[data-expand-default]').forEach((details) => {
        details.open = true;
      });
      this.ensurePriceFilterOpen();
    }

    /**
     * Collapses filter accordions on mobile, except Price which stays open.
     */
    collapseFilters() {
      this.querySelectorAll('.facets__filters details[open]:not([data-type="price_range"])').forEach((details) => {
        details.open = false;
      });
      this.ensurePriceFilterOpen();
    }

    /**
     * Price filter stays expanded on mobile and desktop.
     */
    ensurePriceFilterOpen() {
      this.querySelectorAll('.facets__filters details[data-type="price_range"]').forEach((details) => {
        details.open = true;
      });
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            currentSortBy = evt.detail.selectedValue;
            const hiddenSort = this.form.querySelector('.js-drawer-sort-by');
            if (hiddenSort) hiddenSort.value = currentSortBy;
          }

          // Set the 'sort_by' parameter.
          if (currentSortBy) searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const link = evt.target.closest('a');
      if (!link) return;
      evt.preventDefault();
      this.applyFilters(new URL(link.href).searchParams.toString(), evt);
    }

    /**
     * Handles Clear all clicks (desktop sidebar heading).
     * @param {object} evt - Event object.
     */
    handleClearAllClick(evt) {
      const clearAll = evt.target.closest('a.js-clear-all');
      if (!clearAll) return;
      evt.preventDefault();
      this.applyFilters(new URL(clearAll.href).searchParams.toString(), evt);
    }

    /**
     * Proxies header Reset to Clear all (keeps AJAX path).
     * @param {object} evt - Event object.
     */
    handleResetFiltersClick(evt) {
      evt.preventDefault();
      const clearAll = this.querySelector('a.js-clear-all');
      if (!clearAll?.href) return;
      this.applyFilters(new URL(clearAll.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     * @param {boolean} [replaceUrl=false] - Replace the current history entry.
     */
    async applyFilters(searchParams, evt, updateUrl = true, replaceUrl = false) {
      try {
        searchParams = FacetFilters.toShopifyParams(searchParams, this.form);
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;
          this.ensurePriceFilterOpen();

          // Sync header Reset visibility with active filters.
          const resetBtn = this.querySelector('.js-reset-filters');
          const newActiveFilters = tmpl.content.querySelector('.facets__active-filters');
          if (resetBtn) {
            resetBtn.hidden = !newActiveFilters || newActiveFilters.hasAttribute('hidden');
          }

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Announce the new result count to screen readers (WCAG 4.1.3).
          FacetFilters.announceResultCount(tmpl, this.results);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams, { replace: replaceUrl });

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Announces the result count on the polite status region after a refine.
     * Prefers the server-provided count (data-num-results), falling back to a
     * DOM count of rendered result items.
     * @param {HTMLTemplateElement} tmpl - Parsed response template.
     * @param {Element} results - The (updated) results container.
     */
    static announceResultCount(tmpl, results) {
      const status = document.getElementById('filter-results-status');
      if (!status) return;

      let count = null;
      const container = tmpl.content.querySelector('[data-num-results]');
      if (container) count = Number.parseInt(container.dataset.numResults, 10);
      if (Number.isNaN(count) || count === null) {
        count = results.querySelectorAll('.js-pagination-result').length;
      }

      const strings = (window.theme && theme.strings) || {};
      const tmplStr = count === 1
        ? (strings.resultCountSingle || '[count] result')
        : (strings.resultCount || '[count] results');
      status.textContent = tmplStr.replace('[count]', count);

      // Keep pencil toolbar product count in sync after refine
      document.querySelectorAll('.js-products-count').forEach((el) => {
        el.dataset.productsCount = String(count);
        const mobileVal = el.querySelector('.products-toolbar__count-mobile .products-toolbar__count-value');
        const desktop = el.querySelector('.products-toolbar__count-desktop');
        if (mobileVal) mobileVal.textContent = String(count);
        if (desktop) {
          const productWord = count === 1 ? 'product' : 'products';
          desktop.textContent = `${count} ${productWord}`;
        }
        const bareVal = el.querySelector(':scope > .products-toolbar__count-value');
        if (bareVal) bareVal.textContent = String(count);
      });
    }

    /**
     * Short public keys present on the rendered filter form.
     * @param {Element} form - Facets form.
     * @returns {Set<string>}
     */
    static prettyKeySet(form) {
      const keys = new Set();
      if (!form) return keys;
      form.querySelectorAll('input[data-filter-key]').forEach((input) => {
        if (input.dataset.filterKey) keys.add(input.dataset.filterKey);
      });
      return keys;
    }

    /**
     * Find the checkbox for a pretty key + live label (or native value).
     * @param {Element} form - Facets form.
     * @param {string} prettyKey - Short key such as `material`.
     * @param {string} label - Human label or native filter value.
     * @returns {Element|null}
     */
    static findPrettyInput(form, prettyKey, label) {
      if (!form || !prettyKey) return null;
      const needle = String(label).trim().toLowerCase();
      return [...form.querySelectorAll('input[data-filter-key]')].find((input) => {
        if (input.dataset.filterKey !== prettyKey) return false;
        const prettyLabel = (input.dataset.filterLabel || '').trim().toLowerCase();
        const nativeValue = (input.value || '').trim().toLowerCase();
        return prettyLabel === needle || nativeValue === needle;
      }) || null;
    }

    /**
     * Convert Shopify `filter.*` params (and GIDs) to short human params.
     * @param {string} searchParams - Native or mixed query string.
     * @param {Element} form - Facets form.
     * @returns {string}
     */
    static toPrettyParams(searchParams, form) {
      const input = new URLSearchParams(searchParams || '');
      const output = new URLSearchParams();
      if (!form) return input.toString();

      const filterInputs = [...form.querySelectorAll('input[data-filter-key]')];
      input.forEach((value, key) => {
        let prettyInput = filterInputs.find((el) => el.name === key && el.value === value);
        if (!prettyInput && key.startsWith('filter.')) {
          const prettyKey = filterInputs.find((el) => el.name === key)?.dataset.filterKey;
          if (prettyKey) prettyInput = FacetFilters.findPrettyInput(form, prettyKey, value);
        }
        if (prettyInput && prettyInput.dataset.filterKey && prettyInput.dataset.filterLabel) {
          output.append(prettyInput.dataset.filterKey, prettyInput.dataset.filterLabel);
          return;
        }
        output.append(key, value);
      });

      return output.toString();
    }

    /**
     * Convert short human params back to native Shopify filter params.
     * Native `filter.*` keys pass through unchanged.
     * @param {string} searchParams - Pretty, native, or mixed query string.
     * @param {Element} form - Facets form.
     * @returns {string}
     */
    static toShopifyParams(searchParams, form) {
      const input = new URLSearchParams(searchParams || '');
      const output = new URLSearchParams();
      if (!form) return input.toString();

      const prettyKeys = FacetFilters.prettyKeySet(form);
      input.forEach((value, key) => {
        if (!prettyKeys.has(key)) {
          output.append(key, value);
          return;
        }
        const prettyInput = FacetFilters.findPrettyInput(form, key, value);
        if (prettyInput) {
          output.append(prettyInput.name, prettyInput.value);
        }
      });

      return output.toString();
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * History state keeps native Shopify params for fetch/back-button.
     * @param {string} searchParams - Filter/sort parameters.
     * @param {object} [options]
     * @param {boolean} [options.replace=false]
     */
    static updateURL(searchParams, options = {}) {
      const form = document.getElementById('facets');
      const shopify = FacetFilters.toShopifyParams(searchParams, form);
      const pretty = FacetFilters.toPrettyParams(shopify, form);
      const url = `${window.location.pathname}${pretty ? `?${pretty}` : ''}`;
      const state = { searchParams: shopify };
      if (options.replace) {
        window.history.replaceState(state, '', url);
      } else {
        window.history.pushState(state, '', url);
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
